import React from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, HeartHandshake } from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';
import Button from '../../common/Button';
import dedicatedSupportTeam from '../../../utils/images/dedicated_support_team.png'; // Adjust the path as needed

const Support = () => {
  return (
    <section className="py-20 bg-gray-50">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Customer Support That Goes Above and Beyond"
                subtitle="We're committed to providing excellent support services that keep your business running smoothly."
              />
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start">
                  <div className="mt-1 mr-4 text-blue-600">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">24/7 Technical Assistance</h3>
                    <p className="text-gray-600">
                      Our technical support team is available around the clock to address any issues you might encounter.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mt-1 mr-4 text-blue-600">
                    <CheckCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Preventive Maintenance</h3>
                    <p className="text-gray-600">
                      Regular check-ups and maintenance to prevent problems before they affect your operations.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="mt-1 mr-4 text-blue-600">
                    <HeartHandshake size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Custom Care Packages</h3>
                    <p className="text-gray-600">
                      Tailored support plans designed specifically for your business needs and requirements.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button to="/services" variant="primary">
                Learn More About Our Support
              </Button>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-xl">
              <div className="relative aspect-video rounded-md overflow-hidden mb-6 flex items-center justify-center bg-blue-50">
                <img
                  src={dedicatedSupportTeam}
                  alt="Dedicated Support Team"
                  className="object-cover w-full h-full"
                  style={{ objectPosition: 'center 20%' }}
                />
              </div>
              
              <h3 className="text-xl font-bold mb-3">Dedicated Support Team</h3>
              <p className="text-gray-600 mb-4">
                Our support specialists are trained to handle complex IT issues efficiently and provide 
                clear communication throughout the resolution process.
              </p>
              
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">99</div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Customer Satisfaction</p>
                      <p className="font-bold">99% Rating</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="bg-teal-500 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                      <Clock size={24} />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-500">Response Time</p>
                      <p className="font-bold">Under 1 hour</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -z-10 -top-6 -right-6 w-32 h-32 bg-blue-100 rounded-full opacity-70"></div>
            <div className="absolute -z-10 -bottom-6 -left-6 w-24 h-24 bg-teal-100 rounded-full opacity-70"></div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default Support;